/* eslint-disable @typescript-eslint/no-shadow */
import React, { useCallback } from 'react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import type { DropResult } from 'react-beautiful-dnd'
import { VStack, Box } from '@chakra-ui/react'

interface Props {
  currentItems: string[]
  spacing: number
  isDisabled?: boolean
  setCurrentItems(newValue: string[]): void
  render(sourceId: string | number, index: number): React.ReactElement
}

const reorder = (
  list: string[],
  startIndex: number,
  endIndex: number,
): string[] => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}

/* Generic component. Can handle any vertical drag-and-droppable list */
const DragDropList = ({
  currentItems,
  setCurrentItems,
  render,
  spacing,
  isDisabled,
}: Props): React.ReactElement => {
  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return
      }

      if (result.source.index === result.destination.index) {
        return
      }

      const newItems = reorder(
        currentItems,
        result.source.index,
        result.destination.index,
      )

      setCurrentItems(newItems)
    },
    [currentItems],
  )

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {provided => (
          <VStack
            spacing={spacing}
            alignItems="start"
            width="full"
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {currentItems.map((item, index) => (
              <Draggable isDragDisabled={isDisabled} key={item} draggableId={item} index={index}>
                {innerProvided => (
                  <Box
                    width="full"
                    ref={innerProvided.innerRef}
                    {...innerProvided.draggableProps}
                    {...innerProvided.dragHandleProps}
                  >
                    {render(item, index)}
                  </Box>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </VStack>
        )}
      </Droppable>
    </DragDropContext>
  )
}

export default DragDropList
